/**
 * Integral Delta trades, drawn on the price chart.
 *
 * <h2>Why this exists at all</h2>
 *
 * The backtest's numbers were bad, and a table of 1,568 rows cannot say WHY. Refining the stop
 * multiples means seeing where a trade entered, where it was taken out, and what the bars around it
 * were doing — which is a question about the chart, not about the summary. This is the bridge.
 *
 * <h2>Three drawings, one overlay</h2>
 *
 * A connector from entry to exit, a marker at each end, and the exit reason on hover. The connector
 * is what makes a trade legible as a trade: two loose markers on an 11,000-bar chart are two dots,
 * and nothing says which exit belongs to which entry. That is now ALL it says — it is a subtle
 * dashed grey, identical for a win and a loss. It used to be a thick green or red line and
 * carried the outcome too; see {@link TRADE_LINE_STYLE} for why that was given up and where the
 * outcome is read from instead.
 *
 * <h2>Colour says outcome on the MARKERS; SHAPE says direction</h2>
 *
 * Not both in colour. A long and a short both win and both lose, so four colours would be needed to
 * carry the pair, and on a candlestick chart green and red already mean up and down — a red marker
 * on a winning short would read as a loss to everyone who did not read this file. So the triangle
 * points the way the trade was taken (up for a long, down for a short) and the colour is reserved
 * for whether it worked.
 *
 * <h2>Pure</h2>
 *
 * A report and the chart's bars in, an overlay out. No network, no ECharts, no React.
 */

/** Winners and losers. A breakeven exit is not a win — it claimed a move and returned nothing. */
export const TRADE_COLORS = {
  win: '#2e7d32',
  loss: '#c62828',
  /** Still open when the data ran out: an outcome the market did not produce. */
  open: '#78909c',
};

/**
 * How each exit reads in a tooltip, in the order the state machine reaches them.
 *
 * <h2>Every constant the engine can produce must be here</h2>
 *
 * A missing key falls back to the raw enum name in the tooltip and drops the row out of the
 * Journal's breakdown entirely - so a whole exit category becomes invisible while the trades it
 * ended go on being counted in the totals. Four were already missing before the two new ones were
 * added, which is exactly how that goes unnoticed.
 */
export const EXIT_LABELS = {
  INITIAL_STOP: 'origin shield (1% of entry)',
  TRENDLINE_EXIT: 'closed through the pivot trendline',
  SR_TARGET: 'reached a PTA/PTB level and turned back from it',
  FIB_EXTENSION_TARGET: 'reached the Fibonacci extension of the original leg',
  NEXT_CTS_POINT: 'the next CTS point printed',
  MIDPOINT_REVERSAL: 'the mid-point of a CTS pair - the opposite trade opened here',
  CTS_PAIR_CLOSE: 'the right-hand point of a CTS pair',
  BREAKEVEN_STOP: 'came back to the stop raised to entry at 1R',
  MFE_TRAIL: 'gave back half its best after reaching 2R',
  END_OF_DATA: 'still open — the data ended',
};

export const INTEGRAL_DELTA_TRADES_OVERLAY_ID = 'integral-delta-trades';

/**
 * Cap on how many trades are drawn at once.
 *
 * <h2>THIS WAS 250, AND IT IS THE TRUNCATION</h2>
 *
 * Measured against real runs, keeping the most recent 250 covers only the last six years:
 *
 * <pre>
 *   SPY    1,166 trades 1993..2026   the last 250 reach back only to 2020-02-05
 *   AAPL   2,255 trades 1981..2026   ...to 2020-02-25
 *   IBM    1,998 trades 1970..2026   ...to 2019-08-09
 * </pre>
 *
 * That is the reported symptom exactly — decorations from roughly 2020 to today and nothing
 * before. The old note defended the cap on the grounds that the reason to look is usually to see
 * what the strategy is doing NOW, and offered "narrow the date range to see an older stretch".
 * Both are true and neither is an answer: a chart that silently draws a sixth of its own history
 * is not showing you the recent part, it is hiding the rest, and `countUndrawn` was a number
 * nobody reads on a chart everybody looks at.
 *
 * <p>Ten thousand clears a full-history run on every symbol in the store with room to spare, so in
 * practice nothing is dropped. It is kept as a number rather than removed outright because it is
 * still a runaway guard: something that produced a hundred thousand trades would be a bug, and
 * drawing them would hang the tab rather than report it.
 *
 * <p><b>The cost is real and is accepted.</b> 2,255 trades is 2,255 connectors and 4,510 markers,
 * and a chart carrying that will pan less smoothly than one carrying 250. Correctness about what
 * happened beats smoothness while looking at it. `opts.limit` still narrows it for a caller that
 * wants the recent view.
 */
export const MAX_DRAWN_TRADES = 10000;

/** Which colour an outcome gets. */
/**
 * How the line joining an entry to its exit is drawn, by outcome.
 *
 * <h2>This was one black line, and the reversal is deliberate</h2>
 *
 * Black was chosen so the connector said only WHICH entry belongs to which exit, leaving the
 * verdict to the markers at each end. That reasoning holds when you are reading one trade. It
 * stops holding at the density this chart now carries: with the 250-trade cap gone a full-history
 * run draws over a thousand connectors, and at that scale the eye reads the LINES, not the small
 * glyphs at their ends.
 *
 * <h2>ONE PEN NOW: a solid slate grey, the same for a win and a loss</h2>
 *
 * They were a thick solid green and a thick solid red. The connector is the LEAST informative
 * thing on a trade — it is a straight line between two points that are each already marked — and
 * at a thousand of them it was the loudest, drawing a coloured web over the price action that the
 * markers and the cards explain far better.
 *
 * <p>It was briefly DASHED at half opacity, which overshot: a dashed line at this density breaks
 * into specks and reads as something provisional rather than as structure. Solid, at 2px and 0.8,
 * in a light slate — confident and neutral, which is the register the rest of the chart is now
 * in. Dropping the colour was the change that mattered; making it faint was not part of it.
 *
 * <p><b>THE COST IS REAL AND IS STATED:</b> outcome is no longer encoded on the connector at all.
 * It is still carried twice — the exit MARKER keeps {@link outcomeColor}, and an open trade card
 * accents its return green or red — so nothing is lost that was not said somewhere better. But a
 * reader who learned to read this chart by the colour of its web has to relearn it, and a
 * greyscale print now says nothing about outcome from the lines (it never did, either).
 *
 * <p>{@code tradeLineStyle} still takes the trade and still branches, so restoring an
 * outcome-coloured pen is editing this object rather than rewiring the call sites.
 */
export const TRADE_LINE_STYLE = {
  win: { color: '#778899', width: 2, type: 'solid', opacity: 0.8 },
  loss: { color: '#778899', width: 2, type: 'solid', opacity: 0.8 },
};

/**
 * Which style a trade's connector takes.
 *
 * <p>Strictly greater than zero is a win. A trade that ended exactly where it started returned
 * nothing, and drawing it in the winning pen would overstate the strategy by every scratch it
 * ever took - which is the same rule {@code TradeResult.win()} applies on the engine side.
 */
export function tradeLineStyle(trade) {
  return (trade && trade.pnl > 0) ? TRADE_LINE_STYLE.win : TRADE_LINE_STYLE.loss;
}

export function outcomeColor(trade) {
  if (!trade) return TRADE_COLORS.loss;
  if (trade.reason === 'END_OF_DATA') return TRADE_COLORS.open;
  return trade.pnl > 0 ? TRADE_COLORS.win : TRADE_COLORS.loss;
}

const fmt = (v, dp = 2) => (Number.isFinite(v) ? v.toFixed(dp) : '—');

/** Green for a gain, red for a loss — the same pair the connectors use. */
export const PNL_COLORS = { gain: TRADE_COLORS.win, loss: TRADE_COLORS.loss };

/**
 * The trade's net result, computed from the marker's OWN payload.
 *
 * <pre>
 *   LONG    exit - entry        ((exit - entry) / entry) * 100
 *   SHORT   entry - exit        ((entry - exit) / entry) * 100
 * </pre>
 *
 * <h2>Recomputed here rather than read off `pctReturn`</h2>
 *
 * The backend already sends `pctReturn` and the two agree — deliberately, and there is a test that
 * says so. Deriving it from the three fields the tooltip is already showing makes the card
 * self-consistent: a reader can check the Net line against the entry and exit printed directly
 * above it, and if the backend's figure ever drifted from its own inputs the card would not quietly
 * repeat the drift.
 *
 * <h2>No currency symbol</h2>
 *
 * A "$" would be wrong on more than half this universe — the India list is INR, and the points
 * figure is per unit in whatever the instrument trades in. The sign and the percentage carry the
 * meaning without asserting a currency the chart does not know.
 *
 * @returns {null} when the payload cannot support the arithmetic
 */
export function netResult(trade) {
  const entry = trade?.signal?.entryPrice;
  const exit = trade?.exitPrice;
  if (!Number.isFinite(entry) || !Number.isFinite(exit) || entry === 0) return null;
  const isLong = trade.signal.type === 'BUY';
  const points = isLong ? exit - entry : entry - exit;
  return {
    points,
    percent: (points / entry) * 100,
    // Strictly positive is a gain. A trade that ended exactly where it started returned nothing,
    // and colouring it green would claim otherwise.
    gain: points > 0,
  };
}

/** The Net line as tooltip HTML, coloured by outcome. */
export function netLine(trade) {
  const net = netResult(trade);
  if (!net) return null;
  const sign = net.points > 0 ? '+' : '';
  const color = net.gain ? PNL_COLORS.gain : PNL_COLORS.loss;
  return `<span style="color:${color};font-weight:600">`
    + `Net: ${sign}${fmt(net.points, 4)} (${sign}${fmt(net.percent)}%)</span>`;
}

/**
 * This trade's serial, as it should be shown — or the empty string when there is not one.
 *
 * <h2>Read from the backend, never counted here</h2>
 *
 * The number identifies a trade across every view of the run: the chart draws a subset (anything
 * off the loaded bars is dropped), the Journal prints a sample, and a date range narrows both. A
 * serial derived from a position in whichever list is to hand would differ between them, and a
 * number that changes when you zoom invites exactly the miscount it exists to prevent.
 *
 * <p>Zero, absent or non-numeric prints NOTHING rather than `#0`. That is the old-backend case,
 * and a visible `#0` on every trade would read as a real serial that had gone wrong.
 */
export function tradeSerial(trade) {
  const n = trade?.seq;
  return Number.isFinite(n) && n > 0 ? `#${n}` : '';
}


/**
 * THE EXIT, AND WHETHER IT FILLED WHERE ITS RULE SAID.
 *
 * <p>A 1% stop that lost 1.21R reads as broken arithmetic. It is not: the session opened through
 * the level, nothing could have been filled there, and the open is the first price that existed.
 * Every stop in this engine promises its level EXCEPT across a gap, and that exception used to be
 * invisible - so the only way to tell a gap from a bug was to go and look at the bar.
 *
 * <p>`(GAP)` is appended rather than given its own exit reason, so the breakdown stays groupable:
 * a BREAKEVEN_STOP that gapped is still a breakeven stop.
 */
export function exitDescription(trade) {
  const base = EXIT_LABELS[trade?.reason] || trade?.reason || 'unknown';
  return trade?.gappedFill ? `${base} — GAPPED past the level` : base;
}

/** The short form, for a table: `BREAKEVEN_STOP (GAP)`. */
export function exitTag(trade) {
  return trade?.gappedFill ? `${trade.reason} (GAP)` : String(trade?.reason || '');
}

/** Everything worth knowing about one trade, as tooltip lines. */
export function describeTrade(trade) {
  const sig = trade.signal || {};
  const isLong = sig.type === 'BUY';
  const open = trade.reason === 'END_OF_DATA';
  const serial = tradeSerial(trade);
  return [
    // The serial leads the card: it is the one field a reader is carrying between the chart and
    // the Journal, and it is useless if they have to hunt for it on every hover.
    `<b>${serial ? `${serial} · ` : ''}${isLong ? 'LONG' : 'SHORT'} ${sig.source || ''}</b>`,
    `  entry ${sig.date} @ ${fmt(sig.entryPrice, 4)}`,
    `  exit  ${trade.exitDate} @ ${fmt(trade.exitPrice, 4)}`,
    `  ${exitDescription(trade)} after ${trade.barsHeld} bars`,
    // R first: it is the comparable figure, and the percentage is what the fixed-capital summary
    // adds up. The raw pnl is last because on this chart it is the least informative of the three.
    `  ${fmt(trade.rMultiple)}R  ·  ${fmt(trade.pctReturn)}%  ·  ${fmt(trade.pnl, 4)}/unit`,
    `  ATR at entry ${fmt(trade.atrAtEntry, 4)}`,
    // Point B is half the definition of the stop this trade was held to. Without it the exit price
    // cannot be checked against the rule that produced it, which is the whole question on a chart
    // built for visual debugging.
    ...(trade.pointBBarIndex !== null && trade.pointBBarIndex !== undefined
      ? [`  Point B bar ${trade.pointBBarIndex} @ ${fmt(trade.pointBPrice, 4)}`
        + ` · slope ${fmt(trade.trendlineSlope, 4)}/bar`]
      : ['  (no swing formed — held behind the origin shield throughout)']),
    ...(open ? ['  (unresolved — closed at the last bar, not by the market)'] : []),
    ...(sig.detail ? [`  ${sig.detail}`] : []),
    // Last, and the only coloured line on the card. It is the one figure a reader is looking for,
    // and putting it at the bottom means the eye lands on it after the context rather than before.
    ...(netLine(trade) ? [netLine(trade)] : []),
  ];
}

/**
 * The overlay.
 *
 * @param {object} report  the AnalysisReport as the backend serialises it
 * @param {object} barData `{dates: string[]}` — the chart's own x axis
 * @param {object} opts    `{limit}` to override the drawn-trade cap
 *
 * <p>Trades whose entry or exit date is not on the chart are dropped rather than snapped to a
 * neighbouring bar. A marker on the wrong bar is worse than a missing one here: the entire purpose
 * is to check a trade against the bars it actually happened on.
 */
export function buildTradeOverlay(report, barData, { limit = MAX_DRAWN_TRADES } = {}) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const all = report && Array.isArray(report.results) ? report.results : [];
  // A Set, not indexOf per trade: 1,568 trades against 11,500 dates is 36 million comparisons the
  // naive way, on the main thread, every time the overlay is rebuilt.
  const onChart = new Set(dates);

  const drawable = all.filter(t => t && t.signal
    && onChart.has(t.signal.date) && onChart.has(t.exitDate)
    && Number.isFinite(t.signal.entryPrice) && Number.isFinite(t.exitPrice));

  const shown = drawable.slice(-limit);

  // THE CONNECTOR CARRIES THE VERDICT: solid green for a gain, solid red for a loss, both at the
  // same weight so neither outcome is quieter than the other.
  const connectors = shown.map((t) => {
    const style = tradeLineStyle(t);
    return {
      date1: t.signal.date,
      price1: t.signal.entryPrice,
      date2: t.exitDate,
      price2: t.exitPrice,
      color: style.color,
      width: style.width,
      lineType: style.type,
      opacity: style.opacity,
    };
  });

  const entries = shown.map((t) => {
    const isLong = t.signal.type === 'BUY';
    return {
      date: t.signal.date,
      price: t.signal.entryPrice,
      // The arrow points the way the trade was taken, and sits on the side it points away from, so
      // it never covers the bar it is anchored to.
      position: isLong ? 'below' : 'above',
      symbol: 'triangle',
      symbolSize: 11,
      ...(isLong ? {} : { symbolRotate: 180 }),
      color: outcomeColor(t),
      tooltipLines: describeTrade(t),
    };
  });

  const exits = shown.map(t => ({
    date: t.exitDate,
    price: t.exitPrice,
    // A hollow diamond: distinct from the entry triangle at a glance and at any zoom, and it does
    // not point anywhere — an exit has no direction, and an arrow there would imply one.
    symbol: 'diamond',
    symbolSize: 9,
    hollow: true,
    color: outcomeColor(t),
    tooltipLines: describeTrade(t),
  }));

  return {
    id: INTEGRAL_DELTA_TRADES_OVERLAY_ID,
    type: 'integraldeltatrades',
    label: `Integral Delta trades — ${report?.symbol || ''}`.trim(),
    connectors,
    entries,
    exits,
    drawnCount: shown.length,
    /** Off the chart's own axis, so a date range narrower than the report is counted too. */
    countUndrawn: all.length - shown.length,
    // The serials at each end of what was actually drawn. Null on a run from a backend that does
    // not number its trades, so the status line simply omits the span rather than inventing one.
    firstSerial: shown.length > 0 ? (shown[0].seq || null) : null,
    lastSerial: shown.length > 0 ? (shown[shown.length - 1].seq || null) : null,
  };
}

/** The one-line status the chart shows when the overlay lands. */
export function tradeOverlayStatus(overlay) {
  if (!overlay || overlay.drawnCount === 0) {
    return 'Integral Delta: no trades fall on the bars currently loaded.';
  }
  const undrawn = overlay.countUndrawn > 0
    ? ` (${overlay.countUndrawn} older or off-chart not drawn)` : '';
  // WHICH SERIALS ARE ON SCREEN, not just how many. The drawn set is a window into a longer run,
  // and "412 trades drawn" leaves a reader unable to tell whether they are looking at the start of
  // the history or the end of it. `#755-#1166` says both at once.
  const span = overlay.firstSerial && overlay.lastSerial
    ? ` #${overlay.firstSerial}-#${overlay.lastSerial}` : '';
  return `Integral Delta: ${overlay.drawnCount} trades drawn${span}${undrawn}. `
    + 'Green made money, red did not, grey never closed.';
}
